import React, { useEffect, useMemo, useState } from 'react';
import { useNavigate } from 'react-router-dom';
import LandingShell from '@/components/landing/LandingShell';
import { Card, CardContent, CardFooter, CardHeader, CardTitle } from '@/components/ui/card';
import { Badge } from '@/components/ui/badge';
import {
  Pagination,
  PaginationContent,
  PaginationEllipsis,
  PaginationItem,
  PaginationLink,
  PaginationNext,
  PaginationPrevious,
} from '@/components/ui/pagination';
import { Button } from '@/components/ui/button';
import { Calendar, ExternalLink, Newspaper, Tag, User } from 'lucide-react';
import { supabase } from '@/integrations/supabase/client';
import { useToast } from '@/hooks/use-toast';
import { NewsItem } from '@/types';
import SEO from '@/components/common/SEO';
import { formatContentForDisplay } from '@/utils/htmlUtils';
import { useRouteSsrData } from '@/lib/ssr/context';
import type { NewsPageData } from '@/types/ssr';
import { getNewsSeo } from '@/lib/seo/publicMetadata';
import { formatUtcDate } from '@/lib/ssr/date';

const CATEGORIES = ['Announcements', 'Updates', 'Features', 'Research', 'Community'];

const formatDate = (dateString: string) =>
  formatUtcDate(dateString, {
    year: 'numeric',
    month: 'long',
    day: 'numeric',
  });

const News = () => {
  const initialData = useRouteSsrData<NewsPageData>('news');
  const [newsItems, setNewsItems] = useState<NewsItem[]>(() => (initialData?.items as NewsItem[]) || []);
  const [loading, setLoading] = useState(() => !initialData);
  const { toast } = useToast();
  const navigate = useNavigate();
  const seo = getNewsSeo();
  const itemsPerPage = 6;
  const [currentPage, setCurrentPage] = useState(1);
  const [activeCategory, setActiveCategory] = useState('All');

  useEffect(() => {
    if (initialData) {
      setNewsItems((initialData.items as NewsItem[]) || []);
      setLoading(false);
      return;
    }

    const fetchNews = async () => {
      setLoading(true);
      try {
        const { data, error } = await supabase.from('news').select('*').order('date', { ascending: false });

        if (error) {
          throw error;
        }

        setNewsItems((data || []) as NewsItem[]);
      } catch (error) {
        console.error('Error loading news:', error);
        toast({
          title: 'Error loading news',
          description: 'Could not fetch news from the server.',
          variant: 'destructive',
        });
        setNewsItems([]);
      } finally {
        setLoading(false);
      }
    };

    void fetchNews();
  }, [initialData, toast]);

  const filteredItems = useMemo(
    () => (activeCategory === 'All' ? newsItems : newsItems.filter((news) => news.category === activeCategory)),
    [activeCategory, newsItems]
  );

  const paginatedItems = useMemo(
    () => filteredItems.slice((currentPage - 1) * itemsPerPage, currentPage * itemsPerPage),
    [currentPage, filteredItems]
  );

  const totalPages = Math.ceil(filteredItems.length / itemsPerPage);

  const handlePageChange = (page: number) => {
    setCurrentPage(page);
    window.scrollTo({ top: 0, behavior: 'smooth' });
  };

  const handleCategoryChange = (category: string) => {
    setActiveCategory(category);
    setCurrentPage(1);
  };

  const handleNewsClick = (newsId: string) => {
    navigate(`/news/${newsId}`);
  };

  return (
    <LandingShell>
      <SEO {...seo} />

      <div className="container py-8 px-4 max-w-[1200px] mx-auto">
        <div className="flex items-center justify-between mb-8">
          <div className="flex items-center gap-3">
            <Newspaper size={28} className="text-primary" />
            <h1 className="text-3xl font-bold">News</h1>
          </div>
        </div>

        {loading ? (
          <div className="text-center py-20 border rounded-md bg-muted/20">
            <Newspaper className="mx-auto h-16 w-16 text-muted-foreground opacity-30" />
            <h3 className="mt-6 text-xl font-medium">Loading news...</h3>
          </div>
        ) : newsItems.length === 0 ? (
          <div className="text-center py-20 border rounded-md bg-muted/20">
            <Newspaper className="mx-auto h-16 w-16 text-muted-foreground opacity-30" />
            <h3 className="mt-6 text-xl font-medium">No news yet</h3>
            <p className="mt-2 text-muted-foreground">Check back later for updates about our platform.</p>
          </div>
        ) : (
          <div className="space-y-16">
            <div>
              <div className="flex flex-col sm:flex-row sm:items-center justify-between gap-4 mb-8">
                <div className="flex flex-wrap gap-2">
                  <Badge
                    variant={activeCategory === 'All' ? 'default' : 'outline'}
                    className="cursor-pointer text-sm py-2 px-4"
                    onClick={() => handleCategoryChange('All')}
                  >
                    All
                  </Badge>
                  {CATEGORIES.map((category) => (
                    <Badge
                      key={category}
                      variant={activeCategory === category ? 'default' : 'outline'}
                      className="cursor-pointer text-sm py-2 px-4"
                      onClick={() => handleCategoryChange(category)}
                    >
                      {category}
                    </Badge>
                  ))}
                </div>
              </div>

              <div className="grid grid-cols-1 md:grid-cols-2 lg:grid-cols-3 gap-8 mb-12">
                {paginatedItems.map((news) => (
                  <Card key={news.id} className="overflow-hidden border-0 shadow hover:shadow-md transition-shadow h-full flex flex-col">
                    {news.image_url && (
                      <div className="relative cursor-pointer" onClick={() => handleNewsClick(news.id)}>
                        <Badge className="absolute top-3 left-3 z-10">{news.category || 'General'}</Badge>
                        <div className="group relative">
                          <img
                            src={news.image_url}
                            alt={news.title}
                            className="w-full aspect-video object-cover transition-opacity group-hover:opacity-90"
                          />
                          <div className="absolute inset-0 flex items-center justify-center opacity-0 group-hover:opacity-100 transition-opacity bg-black/20">
                            <ExternalLink className="text-white" size={24} />
                          </div>
                        </div>
                      </div>
                    )}
                    <div className="flex flex-col flex-grow p-6">
                      <CardHeader className="p-0 pb-4">
                        <CardTitle
                          className="text-xl hover:text-primary transition-colors line-clamp-2 cursor-pointer"
                          onClick={() => handleNewsClick(news.id)}
                        >
                          {news.title}
                        </CardTitle>
                        <div className="flex items-center gap-4 text-sm text-muted-foreground mt-2">
                          <div className="flex items-center gap-1">
                            <Calendar size={14} />
                            <span>{formatDate(news.date)}</span>
                          </div>
                        </div>
                      </CardHeader>

                      <CardContent className="p-0 pb-4 flex-grow">
                        <div
                          className="line-clamp-3 text-muted-foreground mt-2"
                          dangerouslySetInnerHTML={{ __html: formatContentForDisplay(news.content) }}
                        />
                      </CardContent>

                      <CardFooter className="p-0 pt-2 flex justify-between items-center">
                        <div className="flex items-center gap-1 text-xs">
                          <User size={12} className="text-muted-foreground" />
                          <span className="text-muted-foreground">{news.author.split(',')[0]}</span>
                        </div>
                        <Button variant="link" className="text-primary font-medium px-0 text-sm" onClick={() => handleNewsClick(news.id)}>
                          Read more
                        </Button>
                      </CardFooter>
                    </div>
                  </Card>
                ))}
              </div>

              {totalPages > 1 && (
                <div className="mt-8">
                  <Pagination>
                    <PaginationContent>
                      <PaginationItem>
                        <PaginationPrevious
                          onClick={() => handlePageChange(Math.max(1, currentPage - 1))}
                          className={currentPage === 1 ? 'pointer-events-none opacity-50' : 'cursor-pointer'}
                        />
                      </PaginationItem>

                      {Array.from({ length: totalPages }).map((_, index) => {
                        const page = index + 1;
                        if (
                          page === 1 ||
                          page === totalPages ||
                          page === currentPage ||
                          page === currentPage - 1 ||
                          page === currentPage + 1
                        ) {
                          return (
                            <PaginationItem key={page}>
                              <PaginationLink isActive={page === currentPage} onClick={() => handlePageChange(page)} className="cursor-pointer">
                                {page}
                              </PaginationLink>
                            </PaginationItem>
                          );
                        }

                        if ((page === 2 && currentPage > 3) || (page === totalPages - 1 && currentPage < totalPages - 2)) {
                          return (
                            <PaginationItem key={`ellipsis-${page}`}>
                              <PaginationEllipsis />
                            </PaginationItem>
                          );
                        }

                        return null;
                      })}

                      <PaginationItem>
                        <PaginationNext
                          onClick={() => handlePageChange(Math.min(totalPages, currentPage + 1))}
                          className={currentPage === totalPages ? 'pointer-events-none opacity-50' : 'cursor-pointer'}
                        />
                      </PaginationItem>
                    </PaginationContent>
                  </Pagination>
                </div>
              )}

              {paginatedItems.length === 0 && filteredItems.length > 0 && (
                <div className="text-center py-12 border rounded-md bg-muted/10 mt-8">
                  <Tag className="mx-auto h-12 w-12 text-muted-foreground opacity-30" />
                  <h3 className="mt-4 text-xl font-medium">No news for this filter</h3>
                  <p className="mt-2 text-muted-foreground">Try selecting a different category</p>
                  <Button variant="outline" className="mt-4" onClick={() => handleCategoryChange('All')}>
                    View All Stories
                  </Button>
                </div>
              )}
            </div>
          </div>
        )}
      </div>
    </LandingShell>
  );
};

export default News;